import { motion } from "framer-motion";
import { FileInput, FileOutput, FileCog } from "lucide-react";

const EASE = [0.16, 1, 0.3, 1];

const FORMATS = [
  { ext: ".pdf", role: "CORE", icon: FileCog, note: "Every engine reads and writes PDF — the native currency of the workspace." },
  { ext: ".docx", role: "OUT", icon: FileOutput, note: "Editable Word export, via the PDF to Word engine." },
  { ext: ".png", role: "OUT", icon: FileOutput, note: "150 DPI page images, via the PDF to PNG engine." },
  { ext: ".txt", role: "OUT", icon: FileOutput, note: "Clean plain text, via the Extract text engine." },
  { ext: ".jpg", role: "IN", icon: FileInput, note: "Scans and photos into PDF, via Images to PDF." },
  { ext: ".webp", role: "IN", icon: FileInput, note: "Web images into PDF, via Images to PDF." },
];

export const Formats = () => (
  <section id="formats" className="py-24 md:py-32 lg:py-40 bg-white border-y border-hairline" data-testid="formats-section">
    <div className="max-w-7xl mx-auto px-6 md:px-12">
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 mb-16 md:mb-20">
        <motion.div
          initial={{ opacity: 0, y: 24, filter: "blur(8px)" }}
          whileInView={{ opacity: 1, y: 0, filter: "blur(0px)" }}
          viewport={{ once: true, margin: "-100px" }}
          transition={{ duration: 1.2, ease: EASE }}
          className="lg:col-span-8"
        >
          <span className="font-mono text-[11px] uppercase tracking-[0.2em] text-mist block mb-5">02 — Formats</span>
          <h2 className="font-medium tracking-tight leading-tight text-3xl md:text-4xl lg:text-5xl text-ink max-w-2xl" data-testid="formats-title">
            Formats are the files. Engines do the work.
          </h2>
        </motion.div>
        <motion.p
          initial={{ opacity: 0, y: 24, filter: "blur(8px)" }}
          whileInView={{ opacity: 1, y: 0, filter: "blur(0px)" }}
          viewport={{ once: true, margin: "-100px" }}
          transition={{ duration: 1.2, ease: EASE, delay: 0.1 }}
          className="lg:col-span-4 text-base text-fog leading-relaxed self-end"
        >
          Six file types flow in and out of the engines above. Everything below is live in the
          workspace today — nothing aspirational, nothing gated.
        </motion.p>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-6" data-testid="formats-grid">
        {FORMATS.map((f, i) => (
          <motion.div
            key={f.ext}
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-40px" }}
            transition={{ duration: 0.7, ease: EASE, delay: (i % 3) * 0.08 }}
            className="border border-hairline rounded-xl p-7 bg-white hover:-translate-y-1 transition-transform duration-500"
            data-testid={`format-card-${f.ext.replace(".", "")}`}
          >
            <div className="flex items-center justify-between mb-6">
              <f.icon className="w-6 h-6 text-ink" strokeWidth={1.5} />
              <span className={`font-mono text-[10px] uppercase tracking-[0.2em] rounded-full px-2.5 py-1 ${f.role === "CORE" ? "text-azure border border-azure/30" : "text-mist border border-hairline"}`}>
                {f.role}
              </span>
            </div>
            <p className="font-mono text-2xl text-ink mb-3">{f.ext}</p>
            <p className="text-sm text-fog leading-relaxed">{f.note}</p>
          </motion.div>
        ))}
      </div>
    </div>
  </section>
);
